import {
  LayoutGrid,
  Package,
  List,
  FlaskConical,
  Activity,
  ArrowLeftRight,
  ArrowDown,
  ArrowUp,
  ClipboardCheck,
  TrendingUp,
  Sliders,
  Package2,
  Wallet,
  CreditCard,
  Coins,
  ShieldCheck,
  AlertTriangle,
  Handshake,
  Users,
  Kanban,
  FileSpreadsheet,
  ListTodo,
  CalendarDays,
  UserPlus,
  FileText,
  Building2,
  Receipt,
  BookOpen,
  Scale,
  StickyNote,
  Mail,
  PackageCheck,
  Boxes,
  type LucideIcon,
} from 'lucide-react'
import { canAccessModule, hasAnyRole, type ModuleKey, type Role } from '@/app/core/auth/useRoles'

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  end?: boolean
  /**
   * Roles allowed to see this page. Omit to inherit module access (visible to
   * anyone who can open the module). Set only where a page is more restricted
   * than its module — mirrors the backend read roles for that page.
   */
  roles?: Role[]
}

export interface NavSection {
  heading?: string
  items: NavItem[]
}

/** Inventory module IA (spec §3). Sections render as labelled groups. */
export const inventoryNav: NavSection[] = [
  {
    items: [{ to: '/', label: 'Dashboard', icon: LayoutGrid, end: true }],
  },
  {
    heading: 'Inventory',
    items: [
      { to: '/products', label: 'Products (SKUs)', icon: Package },
      { to: '/stock', label: 'Stock Levels', icon: List },
      { to: '/batches', label: 'Batches & Expiry', icon: FlaskConical },
      { to: '/movements', label: 'Stock Movements', icon: Activity },
    ],
  },
  {
    heading: 'Operations',
    items: [
      { to: '/receiving', label: 'Receiving', icon: ArrowDown },
      { to: '/dispatch', label: 'Dispatch', icon: ArrowUp },
      { to: '/transfers', label: 'Transfers', icon: ArrowLeftRight },
      { to: '/cycle-counts', label: 'Cycle Counts', icon: ClipboardCheck },
    ],
  },
  {
    heading: 'Quality Control',
    items: [
      // Read-only view of the QC release certificates that gate receiving. Procurement runs the
      // inspections; the warehouse only needs to verify a batch is cleared before accepting it.
      {
        to: '/qc',
        label: 'QC Certificates',
        icon: ShieldCheck,
        roles: ['WAREHOUSE_USER', 'OPS_MANAGER', 'ADMIN', 'AUDITOR'],
      },
    ],
  },
  {
    heading: 'Analysis & Setup',
    items: [
      { to: '/reports', label: 'Reports', icon: TrendingUp, roles: ['OPS_MANAGER', 'ADMIN', 'AUDITOR'] },
      { to: '/statement', label: 'Inventory Statement', icon: FileText, roles: ['OPS_MANAGER', 'ADMIN', 'AUDITOR'] },
      { to: '/settings', label: 'Settings', icon: Sliders },
    ],
  },
]

/** Procurement module IA (spec §3). */
export const procurementNav: NavSection[] = [
  {
    items: [{ to: '/procurement', label: 'Dashboard', icon: LayoutGrid, end: true }],
  },
  {
    heading: 'Procurement',
    items: [
      { to: '/procurement/orders', label: 'Purchase Orders', icon: FileText },
      { to: '/procurement/suppliers', label: 'Suppliers', icon: Building2 },
      { to: '/procurement/bills', label: 'Bills', icon: Receipt, roles: ['PROCUREMENT_MANAGER', 'FINANCE', 'ADMIN', 'AUDITOR'] },
      { to: '/procurement/payments', label: 'Payments', icon: Wallet, roles: ['PROCUREMENT_MANAGER', 'FINANCE', 'ADMIN', 'AUDITOR'] },
    ],
  },
  {
    heading: 'Quality Control',
    items: [
      { to: '/procurement/qc', label: 'QC Inspections', icon: ShieldCheck, roles: ['PROCUREMENT_USER', 'PROCUREMENT_MANAGER', 'ADMIN', 'AUDITOR'] },
      { to: '/procurement/qc/deviations', label: 'Deviations (CAPA)', icon: AlertTriangle, roles: ['PROCUREMENT_USER', 'PROCUREMENT_MANAGER', 'ADMIN', 'AUDITOR'] },
    ],
  },
  {
    heading: 'Analysis & Setup',
    items: [
      { to: '/procurement/reports', label: 'Reports', icon: TrendingUp, roles: ['PROCUREMENT_MANAGER', 'FINANCE', 'ADMIN', 'AUDITOR'] },
      { to: '/procurement/settings', label: 'Settings', icon: Sliders },
    ],
  },
]

/** Order Fulfilment module IA. */
export const ordersNav: NavSection[] = [
  {
    items: [{ to: '/orders', label: 'Orders', icon: PackageCheck, end: true }],
  },
  {
    heading: 'Fulfilment',
    items: [
      { to: '/orders/new', label: 'New B2B Order', icon: Boxes, roles: ['SALES_REP', 'SALES_MANAGER', 'ADMIN'] },
    ],
  },
]

/** Sales (CRM) module IA. */
export const salesNav: NavSection[] = [
  {
    items: [{ to: '/sales', label: 'Dashboard', icon: LayoutGrid, end: true }],
  },
  {
    heading: 'CRM',
    items: [
      { to: '/sales/contacts', label: 'Contacts', icon: Users },
      { to: '/sales/deals', label: 'Deals', icon: Handshake },
      { to: '/sales/pipeline', label: 'Pipeline', icon: Kanban },
      // Partner applications from the public form; BD reviews and approves them here.
      { to: '/sales/onboarding', label: 'Partner onboarding', icon: UserPlus, roles: ['SALES_MANAGER', 'ADMIN', 'AUDITOR'] },
    ],
  },
  {
    heading: 'Quoting',
    items: [
      { to: '/sales/quotes', label: 'Quotes', icon: FileSpreadsheet },
    ],
  },
  {
    heading: 'Operations',
    items: [
      { to: '/sales/tasks', label: 'Tasks', icon: ListTodo },
      { to: '/sales/calendar', label: 'Calendar', icon: CalendarDays },
    ],
  },
  {
    heading: 'Analysis',
    items: [
      { to: '/sales/reports', label: 'Reports', icon: TrendingUp },
    ],
  },
]

/** Accounting (Finance) module IA. */
export const accountingNav: NavSection[] = [
  {
    items: [{ to: '/accounting', label: 'Dashboard', icon: LayoutGrid, end: true }],
  },
  {
    heading: 'Receivables',
    items: [
      { to: '/accounting/invoices', label: 'Invoices', icon: Receipt },
      { to: '/accounting/customers', label: 'Customers', icon: Users },
      { to: '/accounting/notes', label: 'Credit & Debit Notes', icon: StickyNote },
    ],
  },
  {
    heading: 'Ledger',
    items: [
      { to: '/accounting/journal', label: 'Journal Entries', icon: BookOpen },
      { to: '/accounting/accounts', label: 'Chart of Accounts', icon: Scale },
    ],
  },
  {
    heading: 'Analysis',
    items: [
      { to: '/accounting/reports', label: 'Reports', icon: TrendingUp },
    ],
  },
]

/** Admin (Platform) module IA. */
export const adminNav: NavSection[] = [
  {
    items: [{ to: '/admin-portal', label: 'Overview', icon: LayoutGrid, end: true }],
  },
  {
    heading: 'Access',
    items: [
      { to: '/admin-portal/users', label: 'Users', icon: Users },
      { to: '/admin-portal/invitations', label: 'Invitations', icon: Mail },
    ],
  },
  {
    heading: 'Organization',
    items: [
      { to: '/admin-portal/organization', label: 'Organization', icon: Building2 },
      { to: '/admin-portal/integrations', label: 'Integrations', icon: Sliders },
    ],
  },
  {
    heading: 'Governance',
    items: [
      { to: '/admin-portal/audit-logs', label: 'Audit Logs', icon: ShieldCheck },
      { to: '/admin-portal/master-data', label: 'Master Data', icon: List },
    ],
  },
]

export interface PlatformModule {
  key: string
  label: string
  to: string
  icon: LucideIcon
  built: boolean
  blurb: string
}

/** Platform module switcher (spec §2.1). Inventory + Procurement built. */
export const platformModules: PlatformModule[] = [
  { key: 'inventory', label: 'Inventory', to: '/', icon: Package2, built: true, blurb: 'Stock, batches, receiving & fulfilment' },
  { key: 'accounting', label: 'Accounting', to: '/accounting', icon: Wallet, built: true, blurb: 'Ledgers, invoices, payments & reconciliation' },
  { key: 'procurement', label: 'Procurement', to: '/procurement', icon: CreditCard, built: true, blurb: 'Purchase orders, suppliers & vendor payments' },
  { key: 'sales', label: 'Sales', to: '/sales', icon: Coins, built: true, blurb: 'Contacts, pipeline, quotes & tasks' },
  { key: 'orders', label: 'Order Fulfilment', to: '/orders', icon: PackageCheck, built: true, blurb: 'B2B & channel orders, QC, packing & dispatch' },
  { key: 'admin', label: 'Admin Portal', to: '/admin-portal', icon: ShieldCheck, built: true, blurb: 'Users, roles, permissions & org settings' },
]

/** Resolve the active module key from the current path. */
export function moduleForPath(pathname: string): string {
  if (pathname === '/procurement' || pathname.startsWith('/procurement/')) return 'procurement'
  if (pathname === '/orders' || pathname.startsWith('/orders/')) return 'orders'
  if (pathname === '/sales' || pathname.startsWith('/sales/')) return 'sales'
  if (pathname === '/accounting' || pathname.startsWith('/accounting/')) return 'accounting'
  if (pathname === '/admin-portal' || pathname.startsWith('/admin-portal/')) return 'admin'
  return 'inventory'
}

/** Interior nav sections for a module (empty for not-yet-built placeholders). */
export function navForModule(key: string): NavSection[] {
  if (key === 'procurement') return procurementNav
  if (key === 'orders') return ordersNav
  if (key === 'sales') return salesNav
  if (key === 'accounting') return accountingNav
  if (key === 'inventory') return inventoryNav
  if (key === 'admin') return adminNav
  return []
}

/**
 * Nav sections for a module, filtered to the pages the given roles may access.
 * Items with `roles` are hidden unless the user has one of them; sections that
 * end up empty are dropped. Keeps the menu showing only what the user can open.
 */
export function navForModuleFiltered(key: string, roles: readonly string[]): NavSection[] {
  return navForModule(key)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.roles || hasAnyRole(roles, item.roles)),
    }))
    .filter((section) => section.items.length > 0)
}

/** Modules the given roles may see in the switcher (same access map as RoleGuard). */
export function visibleModules(roles: readonly string[]): PlatformModule[] {
  return platformModules.filter((m) => canAccessModule(roles, m.key as ModuleKey))
}
